import { useCallback, useContext, useMemo, useRef } from "react"

import { MarkdownRenderer } from "@/components/markdown-renderer"
import { parseAnswerToHtml } from "@/features/chat/components/answer-parser"
import { createCitationLinkComponent } from "@/features/chat/components/citation-link"
import { CitationPreviewContext } from "@/features/chat/contexts/citation-preview-shared"

export interface WorkspaceMarkdownRendererProps {
  content: string
  citationSection?: string
  className?: string
}

function isWebCitation(citation: string): boolean {
  return citation.startsWith("http://") || citation.startsWith("https://")
}

export function WorkspaceMarkdownRenderer({
  content,
  citationSection = "",
  className,
}: WorkspaceMarkdownRendererProps) {
  const parsed = useMemo(
    () =>
      parseAnswerToHtml({
        answer: content,
        citationSection,
        isStreaming: false,
        noCitation: false,
      }),
    [citationSection, content]
  )
  const citationsRef = useRef(parsed.citations)
  citationsRef.current = parsed.citations

  const components = useMemo(
    () => ({
      a: createCitationLinkComponent(
        () => citationsRef.current,
        () => citationSection
      ),
    }),
    [citationSection]
  )

  // Read the citation preview context directly so this component still
  // renders in isolation (existing unit tests don't wrap a provider).
  const previewContext = useContext(CitationPreviewContext)

  // Delegate clicks on inline `.supContainer` chips (created by
  // createCitationLinkComponent) to citation preview / new-tab. The chat
  // path hooks an equivalent handler at the answer container level
  // (system-message.tsx handleAnswerClick); the workspace path had none,
  // making citations inert.
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement
      const sup = target.closest<HTMLElement>(".supContainer")
      if (!sup) return
      const rawCitation = sup.getAttribute("data-citation-raw")
      if (!rawCitation) return
      if (isWebCitation(rawCitation)) {
        if (typeof window !== "undefined") {
          window.open(rawCitation, "_blank", "noopener,noreferrer")
        }
        return
      }
      previewContext?.openPreview(rawCitation, citationSection)
    },
    [citationSection, previewContext]
  )

  return (
    <div role="presentation" onClick={handleClick}>
      <MarkdownRenderer className={className} components={components} content={parsed.answerHtml} />
    </div>
  )
}
